import { crudGetOne, ListContextProvider, ReduxState, useDeleteWithConfirmController, useListContext, useRefresh } from "ra-core"
import { FC, useCallback, useRef, useState } from "react"
import { Content } from "~/components/content/Content"
import { OdResourcePageProps } from "~/types/resource"
import EditIcon from "@mui/icons-material/Edit"
import AddIcon from "@mui/icons-material/Add"
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import { Pagination as DefaultPagination } from '~/components/list/pagination'
import { Card, Box, IconButton, Tooltip, Typography, Button, CircularProgress } from "@mui/material"
import { Datagrid } from "~/components/list/datagrid"
import Scrollbar from "~/components/Scrollbar"
import { RecPatch } from "~/dataProvider/tools"
import useLocalListController from "~/components/list/useLocalListController"
import { FunctionField, NumberField, TextField } from "~/components/field"
import { DataOwnerGroupResponseDto, OwnerGroupAssignmentBrief } from "~/gen/api/governance"
import Confirm from "~/components/widgets/Confirm"
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers"
import EditDataOwnerGroup from "~/components/governance/EditDataOwnerGroup"
import CreateOrEdit from "~/controller/CreateOrEdit"
import { useResourceRegister } from "~/utils/resourceRegister"
import { useDispatch, useSelector } from "react-redux"
import { useHasPermit } from "~/authProvider/useHasPermit"
import { MANAGE_DATA_OWNER_GROUPS } from "~/constants"
import { crudGetOneEx } from "~/actions/crudFetchEx"


export const listOptions : {sorters: Record<string, (a:DataOwnerGroupResponseDto & RecPatch, b:DataOwnerGroupResponseDto & RecPatch) => number>} = {
  sorters: {
    id: (a, b) => a.id - b.id,
    name: (a, b) => (a.name || '') < (b.name || '') ? -1 : 1,
  }
}

const briefInfoRes = 'settings-data-owner-group-assignment-brief'

const DataOwnerGroupList: FC<OdResourcePageProps> = (props) => {
  const { resource } = props
  useResourceRegister({ name: briefInfoRes })

  const listCtrl = useLocalListController({ resource, basePath: '', filter: { } }, listOptions)
  return (
    <ListContextProvider value={listCtrl}>
      <DataOwnerGroupListBody {...props} />
    </ListContextProvider>
  )
}


const DataOwnerGroupListBody: FC<OdResourcePageProps> = ({
  options,
  resource,
}) => {
  const hasWritePermit = useHasPermit([MANAGE_DATA_OWNER_GROUPS])
  const listCtx = useListContext<DataOwnerGroupResponseDto & RecPatch>()
  
  const [openEdit, setOpenEdit] = useState(false)
  const [recordToEdit, setRecordToEdit] = useState<DataOwnerGroupResponseDto | null>(null)

  const refresh = useRefresh()
  const refreshLight = useCallback(() => refresh(), [refresh])
  const savedHandlers = useDefaultSavedHandlers(() => `${!!recordToEdit ? 'Edit' : 'Create'} Data Owner Group`)

  const recordToDel = useRef<DataOwnerGroupResponseDto&RecPatch | null>(null)
  const delHandlers = useDefaultSavedHandlers('Delete Data Owner Group')
  delHandlers.onSuccess.merge(refreshLight)
  const {
    open: openConfirm,
    handleDelete,
    handleDialogClose,
    handleDialogOpen,
  } = useDeleteWithConfirmController({ 
    record: recordToDel.current as any,
    onClick: () => { recordToDel.current = null },
    ...delHandlers,
  })
  
  const dispatch = useDispatch()
  const queryBrief = (ownerGroupId: number) => {
    dispatch(crudGetOneEx(briefInfoRes, ownerGroupId, ''))
  }
  const briefInfo = useSelector<ReduxState, OwnerGroupAssignmentBrief&RecPatch | null>(store => {
    const resData = store.admin.resources[briefInfoRes]?.data
    if (!resData || !recordToDel.current) { return null }
    return resData[recordToDel.current.id] || null
  })
  const loading = useSelector<ReduxState, boolean>(store => store.admin.loading > 0)
  
  const handlerDeleteOrBlock = (e: any) => {
    if (loading) {
      handleDialogClose(e)
    } else {
      handleDelete(e)
    }
  }
  
  return (
    <Content 
      pageName="Manage Data Owner Groups" 
      resourceName="Data Owner Groups" 
      breadcrumbs={options?.breadcrumbs}
      mainActions={
        hasWritePermit &&
        <Button
          color="primary"
          variant="contained"
          startIcon={
            <AddIcon />
          }
          onClick={() => {
            setRecordToEdit(null)
            setOpenEdit(true)
          }}
        >
          Add Data Owner Group
        </Button>
      }
    >
      <Card>
        <Scrollbar>
          <Box sx={{ minWidth: 1150 }}>
            <Datagrid 
              hasBulkActions={false} 
              sx={{ 
                '& th' : { py: 2 },
                '& td' : { py: 2 },
                '& .name': { maxWidth: 220, display: 'inline-block', wordBreak: 'break-word' },
                '& .description': { maxWidth: 500, display: 'inline-block', wordBreak: 'break-word' },
              }}
              {...listCtx}
            >
              <TextField source="name" label="Name" className="name" />
              <TextField source="description" label="Description" sortable={false} className="description" />
              <NumberField source="ownedTablesCount" label="Number of Owned Tables" sortable={false} textAlign="center" />
              {hasWritePermit &&
                <FunctionField 
                  label="Actions"
                  sortable={false}
                  render={(d?:DataOwnerGroupResponseDto&RecPatch) => (
                    <>
                      <Tooltip title="Edit">
                        <IconButton
                          sx={{
                            color: 'text.primary' as any,
                            '&:hover': { backgroundColor: 'action.hover' },
                          }}
                          onClick={() => {
                            setRecordToEdit(d || null)
                            setOpenEdit(true)
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          sx={{
                            color: 'text.primary' as any,
                            '&:hover': { backgroundColor: 'action.hover' },
                          }}
                          onClick={(e:any) => {
                            recordToDel.current = d as any
                            handleDialogOpen(e)
                            queryBrief(d?.ownerGroupId || 0)
                          }}
                        >
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                />}
            </Datagrid>
          </Box>
        </Scrollbar>
        {//@ts-ignore 
        <DefaultPagination {...listCtx} />}
      </Card>
      {hasWritePermit &&
        <>
          <CreateOrEdit resource={resource} basePath="" record={recordToEdit} onFailure={savedHandlers.onFailure} onSuccess={savedHandlers.onSuccess.merge(refreshLight)}>
            {openEdit && <EditDataOwnerGroup open={openEdit} setOpen={(x) => {setOpenEdit(x); if (!x) { setTimeout(() => setRecordToEdit(null)) }}} />}
          </CreateOrEdit>
          <Confirm
            isOpen={openConfirm}
            title={"Delete Data Owner Group"}
            onConfirm={handlerDeleteOrBlock}
            onClose={handleDialogClose}
            confirm={loading ? "Close" : "Confirm"}
            cancel="Cancel"
            content={(
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 550 }}>
                  {loading
                    ? <CircularProgress sx={{ mx: 'auto', mt: 2 }} />
                    : <Box>
                      {briefInfo && !!briefInfo.totalTableCount &&
                        <>
                          <Typography
                            variant="body2"
                            sx={{ ml: 1 }}
                          >
                            The following tables are owned by this 
                            {recordToDel.current ? ` "${recordToDel.current?.name}" ` : ' '} 
                            data owner group. 
                            The data owner group for these tables will be changed to None upon deletion. 
                          </Typography>
                          <Box 
                            sx={{ 
                              mt: 2,
                              borderRadius: '8px', 
                              px: 3,
                              py: 1,
                              bgcolor: theme => theme.palette.background.default 
                            }}
                          >
                            {briefInfo?.assignedTables?.map((t, i) => (
                              <Typography 
                                key={i}
                                component="p"
                                variant="caption"
                              >
                                {t.databaseName}. {t.tableSchema}. 
                                <b> {t.tableName}</b>
                              </Typography>
                            ))}
                          </Box>
                          {(briefInfo?.totalTableCount || 0) > (briefInfo?.assignedTables || []).length &&
                            <Typography
                              variant="caption"
                              sx={{ ml: 3 }}
                            >
                              And {(briefInfo?.totalTableCount || 0) - (briefInfo?.assignedTables || []).length} more.
                            </Typography>
                          }
                        </>
                      }

                      <Typography
                        variant="body2"
                        sx={{ mt: 2 }}
                      >
                        Please confirm whether to proceed with the deletion of this
                        {recordToDel.current ? ` "${recordToDel.current?.name}" ` : ' '}
                        Data Owner Group.
                      </Typography>
                    </Box>
                  }

                </Box>
              </Box>
            )}
          />
        </>}
    </Content>
  )
}

export default DataOwnerGroupList;
